import { useForm, useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, Pencil } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { usePlan } from '~/app/plan-context'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { eventImagesApi } from '~/entities/event/api'
import { useUpdateEvent } from '~/entities/event/model/use-events'
import { useListings } from '~/entities/listing/model/use-listings'
import { applyAddressVisibility } from '~/features/events/lib/address-visibility'
import { buildEventFormValues } from '~/features/events/lib/event-form-values'
import { applyEventPrivacy } from '~/features/events/lib/event-privacy'
import { useSavedConfirmationStep } from '~/features/events/lib/use-saved-confirmation'
import type { ImageFile } from '~/features/shared/components/images-section'
import { useLeaveGuard } from '~/features/shared/components/leave-guard'
import { saveStagedImages } from '~/features/shared/lib/image-order'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { cn } from '~/shared/lib/utils'
import type { Event } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '~/shared/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/shared/ui/tooltip'
import { coverSubjectOf } from '../ai-flow/cover-chat-button'
import { EventDateTimeSection, type RecurringConfig } from './event-datetime-section'
import { EventDetailsSection } from './event-details-section'
import { EventFeaturesSection } from './event-features-section'
import { EventImagesSection } from './event-images-section'
import { EventLocationSection } from './event-location-section'
import { EventPricingSection } from './event-pricing-section'

type EventEditSection = 'details' | 'images' | 'datetime' | 'location' | 'pricing' | 'features'

const SECTION_TITLE: Record<EventEditSection, () => string> = {
  details: m.description,
  images: m.eventImages,
  datetime: m.dateTime,
  location: m.location,
  pricing: m.ticketsAndPricing,
  features: m.moreSettingsTitle,
}

/** A pencil button that opens just one part of the event form in a dialog. */
export function EventSectionEditButton({
  event,
  section,
  label,
  className,
}: {
  event: Event
  section: EventEditSection
  label?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const text = label ? `${m.edit()}: ${label}` : m.edit()
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={className}
            aria-label={text}
            onClick={() => setOpen(true)}
          >
            <Pencil />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
      {open && (
        <EventSectionEditDialog event={event} section={section} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function EventSectionEditDialog({
  event,
  section,
  onClose,
}: {
  event: Event
  section: EventEditSection
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { listings } = useListings(businessId)
  const { limits } = usePlan()
  const updateEvent = useUpdateEvent(businessId)
  const savedConfirmation = useSavedConfirmationStep(businessId, event.id)
  const [images, setImages] = useState<ImageFile[]>([])
  const [insertPositions, setInsertPositions] = useState<number[]>([])
  const [removedImageIds, setRemovedImageIds] = useState<string[]>([])
  const [savingImages, setSavingImages] = useState(false)
  const [recurringConfig, setRecurringConfig] = useState<RecurringConfig>({
    enabled: false,
    frequencyType: 'weeks',
    interval: 1,
    count: 5,
  })

  const form = useForm({
    defaultValues: buildEventFormValues(event),
    onSubmit: async ({ value }) => {
      try {
        if (section === 'images') await saveImages()
        else
          await updateEvent.mutateAsync({
            eventId: event.id,
            data: applyEventPrivacy(applyAddressVisibility(value, savedConfirmation)),
          })
        leaveGuard.release()
        toast.success(m.eventUpdated())
        onClose()
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  async function saveImages() {
    if (images.length === 0 && removedImageIds.length === 0) return
    setSavingImages(true)
    try {
      await saveStagedImages(eventImagesApi, event.id, {
        existingIds: event.images.map((image) => image.id),
        files: images.map((image) => image.file),
        insertPositions,
        removedIds: removedImageIds,
      })
    } finally {
      setSavingImages(false)
      await queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT, event.id) })
      await queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENTS, businessId) })
    }
  }

  const dirty =
    useStore(form.store, (state) => state.isDirty) ||
    images.length > 0 ||
    removedImageIds.length > 0
  const leaveGuard = useLeaveGuard(dirty, m.leaveUnsavedBody())
  const keepOpenWhileDirty = (event: { preventDefault: () => void }) => {
    if (dirty) event.preventDefault()
  }

  const linkedListingName = event.listingId
    ? (listings.find((listing) => listing.id === event.listingId)?.name ?? null)
    : null

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !form.state.isSubmitting) onClose()
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={keepOpenWhileDirty}
        onEscapeKeyDown={keepOpenWhileDirty}
      >
        {leaveGuard.dialog}
        <DialogTitle className="sr-only">{SECTION_TITLE[section]()}</DialogTitle>
        <form
          className={cn(
            'space-y-4',
            '[&>[data-slot=card]]:border-0 [&>[data-slot=card]]:bg-transparent',
            '[&>[data-slot=card]>[data-slot=card-header]]:pt-0 [&>[data-slot=card]>[data-slot=card-header]]:pr-8 [&>[data-slot=card]>[data-slot=card-header]]:pl-0',
            '[&>[data-slot=card]>[data-slot=card-content]]:px-0 [&>[data-slot=card]>[data-slot=card-content]]:pb-0',
          )}
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          {section === 'details' && <EventDetailsSection form={form} />}
          {section === 'images' && (
            <EventImagesSection
              eventId={event.id}
              existingImages={event.images}
              onImagesChange={(files, _primary, positions?: number[]) => {
                setImages(files)
                setInsertPositions(positions ?? [])
              }}
              onRemovedExistingChange={setRemovedImageIds}
              coverSubject={() => coverSubjectOf(form.state.values)}
            />
          )}
          {section === 'datetime' && (
            <EventDateTimeSection
              form={form}
              recurringConfig={recurringConfig}
              onRecurringConfigChange={setRecurringConfig}
              canRecur={limits.canRecurEvents}
            />
          )}
          {section === 'location' && (
            <EventLocationSection
              form={form}
              isEditMode
              linkedListingName={linkedListingName}
              savedConfirmation={savedConfirmation}
            />
          )}
          {section === 'pricing' && <EventPricingSection form={form} />}
          {section === 'features' && <EventFeaturesSection form={form} />}
          <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
            {([canSubmit, isSubmitting]) => {
              const busy = isSubmitting || savingImages
              return (
                <DialogFooter>
                  <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
                    {m.cancel()}
                  </Button>
                  <Button type="submit" disabled={!canSubmit || busy}>
                    {busy && <Loader2 className="animate-spin" />}
                    {busy ? m.updating() : m.saveChanges()}
                  </Button>
                </DialogFooter>
              )
            }}
          </form.Subscribe>
        </form>
      </DialogContent>
    </Dialog>
  )
}
